import { call, put } from "redux-saga/effects";
import { Types } from "../actionTypes";
import { setCookie, getCookie, removeCookie } from "@Src/utils/cookie.utils";
import API from "@Api";
import { getLanguageFromLocation } from "@Src/components/Language/LanguageByLocationMap";
import actions from "@Src/redux/rootActions";
import { DEFAULT_LOCATION_COOKIE, LANGUAGE_COOKIE } from "@Src/constants";

const { signOutSuccess } = actions.favorites;

export function* completeAccountSetup(action) {
  try {
    const data = yield call(API.member.completeAccountSetup, {
      ...action.memberData,
    });
    yield put({ type: Types.COMPLETE_ACCOUNT_SETUP_SUCCESS, data });
  } catch (error) {
    yield put({ type: Types.COMPLETE_ACCOUNT_SETUP_FAILURE, error });
  }
}

export function* createMember(action) {
  try {
    const data = yield call(API.member.registerNewMember, action.memberData);
    yield put({ type: Types.CREATE_MEMBER_SUCCESS, data });
  } catch (error) {
    console.error(error);
    let safeError = error && error.data;
    yield put({ type: Types.CREATE_MEMBER_FAILURE, error: safeError });
  }
}

export function* signIn(action) {
  try {
    const data = yield call(API.auth.signIn, action.credentials);
    yield put({ type: Types.SIGN_IN_SUCCESS, data });
  } catch (error) {
    yield put({ type: Types.SIGN_IN_FAILURE, error });
  }
}

export function* signOut(action) {
  try {
    const data = yield call(API.auth.signOut, action.credentials);
    if (getCookie(DEFAULT_LOCATION_COOKIE)) {
      removeCookie("default-location"); // keep this for a few weeks in order to remove the old cookie
      removeCookie(DEFAULT_LOCATION_COOKIE);
    }
    yield put({ type: Types.SIGN_OUT_SUCCESS, data });

    // Types from slice
    yield put(signOutSuccess(data));
  } catch (error) {
    yield put({ type: Types.SIGN_OUT_FAILURE, error });
  }
}

export function* doAuthByCvt(action) {
  try {
    const data = yield call(API.auth.authByCvt, action.credentials);
    yield put({ type: Types.SIGN_IN_SUCCESS, data });
  } catch (error) {
    console.error(error);
    yield put({ type: Types.SIGN_IN_FAILURE, error });
  }
}

export function* doAuthBySsoToken(action) {
  try {
    const data = yield call(API.auth.authBySsoToken, action.data);
    yield put({ type: Types.SIGN_IN_SUCCESS, data });
  } catch (error) {
    console.error(error);
    yield put({ type: Types.SIGN_IN_FAILURE, error });
  }
}

export function* doAuthBySlaToken(action) {
  try {
    const data = yield call(API.auth.authBySlaToken, action.data);
    yield put({ type: Types.SIGN_IN_SUCCESS, data });
  } catch (error) {
    console.error(error);
    yield put({ type: Types.SIGN_IN_FAILURE, error });
  }
}

export function* doCompleteMemberProfile(action) {
  try {
    const data = yield call(API.member.completeMemberProfile, action.memberData);
    yield put({ type: Types.COMPLETE_MEMBER_SUCCESS, data });
  } catch (error) {
    console.error(error);
    let safeError = error && error.data;
    yield put({ type: Types.COMPLETE_MEMBER_FAILURE, error: safeError });
  }
}

export function* validateSession() {
  try {
    const data = yield call(API.auth.validateSession);
    yield put({ type: Types.VALIDATE_SESSION_SUCCESS, data });
  } catch (error) {
    yield put({ type: Types.VALIDATE_SESSION_FAILURE, error });
  }
}

export function* fetchMemberAccountInfo() {
  try {
    const data = yield call(API.member.fetchMemberAccountInfo);
    yield put({ type: Types.FETCH_MEMBER_ACCOUNT_SUCCESS, data });
  } catch (error) {
    yield put({
      type: Types.FETCH_MEMBER_ACCOUNT_FAILURE,
      error: error.response,
    });
  }
}

export function* updateMemberAccountInfo(action) {
  try {
    const data = yield call(API.member.updateMemberAccountInfo, action.data);
    const { default_location, use_default_location_on_login } = data.preferences;

    if (default_location && use_default_location_on_login) {
      if (getCookie("default-location")) {
        // keep this for a few weeks in order to remove the old cookie
        removeCookie("default-location");
      }
      setCookie(DEFAULT_LOCATION_COOKIE, JSON.stringify(default_location), 14);
    }

    yield put({ type: Types.UPDATE_MEMBER_ACCOUNT_SUCCESS, data });
  } catch (error) {
    yield put({
      type: Types.UPDATE_MEMBER_ACCOUNT_FAILURE,
      error: error.response,
    });
  }
}

export function* doCheckEmailAvailability(action) {
  try {
    const data = yield call(API.member.checkEmailAvailability, action.email);
    yield put({ type: Types.CHECK_EMAIL_AVAILABILITY_SUCCESS, data });
  } catch (error) {
    console.log(error);
    yield put({
      type: Types.CHECK_EMAIL_AVAILABILITY_FAILURE,
      error: error.data,
    });
  }
}
export function* doCheckRegistrationCode(action) {
  try {
    const data = yield call(API.member.checkRegistrationCode, action.code);
    yield put({ type: Types.CHECK_REGISTRATION_CODE_SUCCESS, data });
  } catch (error) {
    console.log(error);
    yield put({
      type: Types.CHECK_REGISTRATION_CODE_FAILURE,
      error: error.data,
    });
  }
}
export function* doCheckMemberId(action) {
  try {
    const data = yield call(API.member.checkMemberId, action.memberId);
    yield put({ type: Types.CHECK_MEMBER_ID_SUCCESS, data });
  } catch (error) {
    console.log(error);
    yield put({
      type: Types.CHECK_MEMBER_ID_FAILURE,
      error: error.data,
    });
  }
}

export function* getDefaultLocation() {
  try {
    const data = yield call(API.member.fetchMemberDefaultLocation);
    const { default_location, use_default_location_on_login, default_country } = data;

    // Set default location in filter and as a cookie if its not already there
    if (default_location && use_default_location_on_login && !getCookie(DEFAULT_LOCATION_COOKIE)) {
      if (getCookie("default-location")) {
        // keep this for a few weeks in order to remove the old cookie
        removeCookie("default-location");
      }
      setCookie(DEFAULT_LOCATION_COOKIE, JSON.stringify(default_location), 14);
      yield put({ type: Types.SELECT_LOCATION, location: default_location });
    }

    // set default language if country code exists within the address block
    const localeCookie = getCookie(LANGUAGE_COOKIE);
    if (default_country && !localeCookie) {
      yield put({
        type: Types.UPDATE_LANGUAGE,
        language: getLanguageFromLocation({
          country: default_country,
          state_region: "",
        }),
      });
    }

    yield put({ type: Types.FETCH_MEMBER_DEFAULT_LOCATION_SUCCESS, data });
  } catch (error) {
    yield put({
      type: Types.FETCH_MEMBER_DEFAULT_LOCATION_FAILURE,
      error: error.response,
    });
  }
}
